//React
import * as React from "react";
import { useState } from "react";
//Material UI
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
//Routing
import { Outlet, useLocation, useNavigate } from "react-router-dom";
//Redux
import { useDispatch } from "react-redux";
import { logout } from "../../Features/Authentication/authSlice";
import { useGetUserDetailsQuery } from "../../Features/api/apiSlice";
//Components
import tjommieLogo from "../images/tjommie-logo.svg";
import Footer from "../footer/Footer";
import {
  ChartIcon,
  ChevronLeftIcon,
  CloseIcon,
  GridIcon,
  LogoutIcon,
  MenuIcon,
  UserIcon,
  UsersIcon,
} from "./icons";

/**
 * Shared application chrome: fixed top bar, collapsible sidebar, footer.
 *
 * Replaces DashboardPageNavbar and UsersNavbar, which were ~98% byte-identical
 * copies differing only in one word of the wordmark and a commented-out
 * hamburger — and which both exported a function named `UsersNavbar`.
 *
 * Mounted as a react-router layout route so the sidebar offset lives in one
 * place. Previously each route composed its own `<><Navbar/><Page/></>` pair,
 * so there was nowhere to hook a page-level wrapper.
 *
 * Built to NeuraDesigns `userport/index.html` + `assets/neura.css`.
 */

const SIDEBAR_W = 280;
const SIDEBAR_W_COLLAPSED = 64;
const HEADER_H = 60;
const COLLAPSE_KEY = "userport.sidebar.collapsed";

/** Every route here sits behind the same PermissionRedirection gate, so a user
 *  who can reach one can reach all of them — no per-item permission check. */
const NAV_ITEMS = [
  { label: "Dashboard", to: "/", Icon: GridIcon },
  { label: "User Management", to: "/userport", Icon: UsersIcon },
  { label: "Residents Analytics", to: "/residents-analytics", Icon: ChartIcon },
];

const TITLES = {
  "/": "BusiOpsMan",
  "/userport": "Userport",
  "/clientapps": "Client Apps",
  "/residents-analytics": "Residents Analytics",
  "/profile": "Profile",
};

function readCollapsed() {
  try {
    const stored = window.localStorage.getItem(COLLAPSE_KEY);
    // The design ships the rail collapsed, so that is the default until the
    // user expresses a preference; after that their choice persists.
    return stored === null ? true : stored === "true";
  } catch (e) {
    // Private browsing / blocked storage. The rail still works, it just
    // forgets between loads.
    return true;
  }
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Drives the avatar initial. Cached by RTK Query, so this is one request for
  // the whole session rather than one per route.
  const { data: userData } = useGetUserDetailsQuery({});
  const details = userData && userData.data;
  const initial = (
    (details && (details.first_name || details.email)) ||
    ""
  ).charAt(0).toUpperCase();

  const title = TITLES[location.pathname] || "BusiOpsMan";

  const toggleCollapsed = () => {
    setCollapsed((wasCollapsed) => {
      const next = !wasCollapsed;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch (e) {
        /* storage unavailable — keep the in-memory state */
      }
      return next;
    });
  };

  const go = (to) => {
    setMobileOpen(false);
    navigate(to);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    dispatch(logout());
  };

  const railWidth = { xs: SIDEBAR_W_COLLAPSED, md: collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W };

  return (
    <>
      {/* ---------------------------------------------------------------- */}
      {/* Top bar                                                           */}
      {/* ---------------------------------------------------------------- */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          height: `${HEADER_H}px`,
          background: "var(--surface)",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          padding: "0 var(--header-pad-x)",
          gap: "var(--space-4)",
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileOpen((open) => !open)}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            alignItems: "center",
            justifyContent: "center",
            width: 36,
            height: 36,
            border: "1px solid var(--border)",
            background: "transparent",
            borderRadius: "8px",
            color: "var(--text-secondary)",
            cursor: "pointer",
            transition: "all 0.18s ease",
            "&:hover": { color: "var(--accent)", borderColor: "rgba(253, 185, 64, 0.3)" },
          }}
        >
          <MenuIcon width={18} height={18} />
        </Box>

        <Box
          component="img"
          src={tjommieLogo}
          alt="Tjommie"
          sx={{ height: "var(--header-logo-h)", display: "block" }}
        />

        <Box sx={{ width: "1px", height: 22, background: "var(--border-strong)", margin: "0 4px" }} />

        <Box
          component="span"
          sx={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "var(--text-secondary)",
            letterSpacing: "-0.005em",
          }}
        >
          {title}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          component="button"
          type="button"
          aria-label="Account"
          aria-haspopup="true"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "none",
            background: "linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%)",
            color: "var(--bg)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.78rem",
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {initial || <UserIcon width={16} height={16} />}
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate("/profile");
            }}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 30 }}>
              <UserIcon width={16} height={16} />
            </ListItemIcon>
            Profile
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon sx={{ color: "inherit", minWidth: 30 }}>
              <LogoutIcon width={16} height={16} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Mobile backdrop                                                   */}
      {/* ---------------------------------------------------------------- */}
      <Box
        onClick={() => setMobileOpen(false)}
        sx={{
          display: { xs: mobileOpen ? "block" : "none", md: "none" },
          position: "fixed",
          inset: `${HEADER_H}px 0 0 0`,
          zIndex: 49,
          background: "rgba(0, 0, 0, 0.55)",
        }}
      />

      {/* ---------------------------------------------------------------- */}
      {/* Sidebar                                                           */}
      {/* ---------------------------------------------------------------- */}
      <Box
        component="aside"
        sx={{
          position: "fixed",
          top: `${HEADER_H}px`,
          left: 0,
          bottom: 0,
          zIndex: 40,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: { xs: mobileOpen ? "var(--surface)" : "transparent", md: "transparent" },
          borderRight: { xs: mobileOpen ? "1px solid var(--border)" : "none", md: "none" },
          width: { xs: mobileOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED, md: railWidth.md },
          transition: "width 0.22s var(--ease-out)",
        }}
      >
        {/* Toggle row. `order: -1` pins it to the top of the rail, per the design. */}
        <Box
          sx={{
            order: -1,
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)",
            padding: collapsed ? "var(--space-3) 0" : "var(--space-3)",
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: "var(--radius-md)",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              transition: "all 0.18s var(--ease-out)",
              "&:hover": { color: "var(--accent)", borderColor: "rgba(253, 185, 64, 0.3)" },
            }}
          >
            <ChevronLeftIcon
              width={16}
              height={16}
              style={{
                transition: "transform 0.22s var(--ease-out)",
                transform: collapsed ? "rotate(180deg)" : "none",
              }}
            />
          </Box>

          {/* Mobile close button, in place of the desktop collapse chevron. */}
          <Box
            component="button"
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            sx={{
              display: { xs: mobileOpen ? "inline-flex" : "none", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: "var(--radius-md)",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            <CloseIcon width={16} height={16} />
          </Box>

          {!collapsed && (
            <Box
              component="span"
              sx={{
                display: { xs: "none", md: "block" },
                fontSize: "0.72rem",
                color: "var(--text-muted)",
                flex: 1,
              }}
            >
              Collapse
            </Box>
          )}
        </Box>

        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            padding: "8px",
            gap: "2px",
            overflowY: "auto",
          }}
        >
          {NAV_ITEMS.map(({ label, to, Icon }) => {
            const active = location.pathname === to;
            const showLabel = mobileOpen || !collapsed;
            const item = (
              <Box
                key={to}
                component="button"
                type="button"
                onClick={() => go(to)}
                aria-current={active ? "page" : undefined}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  padding: showLabel ? "10px 12px" : "10px 0",
                  justifyContent: showLabel ? "flex-start" : "center",
                  border: "none",
                  borderRadius: "8px",
                  background: active ? "var(--accent-tint-strong)" : "transparent",
                  color: active ? "var(--accent)" : "var(--text-secondary)",
                  fontFamily: "inherit",
                  fontSize: "0.88rem",
                  fontWeight: 500,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    background: active ? "var(--accent-tint-strong)" : "var(--accent-tint)",
                    color: active ? "var(--accent)" : "var(--text)",
                  },
                  "& svg": { flexShrink: 0 },
                }}
              >
                <Icon width={18} height={18} />
                {showLabel && <span>{label}</span>}
              </Box>
            );

            // Collapsed rail shows the label as a tooltip instead.
            return showLabel ? (
              item
            ) : (
              <Tooltip key={to} title={label} placement="right">
                {item}
              </Tooltip>
            );
          })}
        </Box>
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Content                                                           */}
      {/* ---------------------------------------------------------------- */}
      <Box
        component="main"
        sx={{
          paddingTop: `${HEADER_H}px`,
          marginLeft: { xs: `${SIDEBAR_W_COLLAPSED}px`, md: `${railWidth.md}px` },
          transition: "margin-left 0.22s var(--ease-out)",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>
        <Footer />
      </Box>
    </>
  );
}
